import { toast } from '@repo/design-system/components/ui/toaster';
import { useAppDispatch, useAppSelector } from '@repo/store/maps/hooks';
import { selectExternalMapId } from '@repo/store/maps/slices/mapData';
import { cancelAddingMapObject3d, createMapObject3d, selectIsAddingMapObject3d, setIsAddingMapObject3d, setSelectedMapObject3dId } from '@repo/store/maps/slices/mapObjects3d';
import type { MapData3dObject } from '@repo/types/maps/map-3d-types';
import type maplibregl from 'maplibre-gl';
import { useCallback, useRef } from 'react';
import { useMapContext } from '../map-context';
import { useMapCursor } from './useMapCursor';
export function useObject3dPlacement() {
  const {
    map
  } = useMapContext();
  const dispatch = useAppDispatch();
  const externalId = useAppSelector(selectExternalMapId);
  const {
    setCursor
  } = useMapCursor();
  const isAdding3dObject = useAppSelector(selectIsAddingMapObject3d);
  const eventHandlersRef = useRef<{
    mapClick: ((e: maplibregl.MapMouseEvent) => void) | null;
    escape: ((e: KeyboardEvent) => void) | null;
    clickOutside: ((e: MouseEvent) => void) | null;
  }>({
    mapClick: null,
    escape: null,
    clickOutside: null
  });
  const cleanupEventListeners = useCallback(() => {
    const {
      mapClick,
      escape,
      clickOutside
    } = eventHandlersRef.current;
    if (map && mapClick) {
      map.off('click', mapClick);
      eventHandlersRef.current.mapClick = null;
    }
    if (escape) {
      document.removeEventListener('keydown', escape);
      eventHandlersRef.current.escape = null;
    }
    if (clickOutside) {
      document.removeEventListener('click', clickOutside);
      eventHandlersRef.current.clickOutside = null;
    }
    setCursor('grab');
  }, [map, setCursor]);
  const createSharedHandlers = useCallback((onCancel: () => void) => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    };
    const handleClickOutside = (e_0: MouseEvent) => {
      const target = e_0.target as Element;
      const mapContainer = map?.getContainer();
      if (mapContainer && !mapContainer.contains(target)) {
        onCancel();
      }
    };
    return {
      handleEscape,
      handleClickOutside
    };
  }, [map]);
  const setupEventListeners = useCallback((mapClickHandler: (e: maplibregl.MapMouseEvent) => void, onCancel_0: () => void) => {
    if (!map) return;
    const {
      handleEscape: handleEscape_0,
      handleClickOutside: handleClickOutside_0
    } = createSharedHandlers(onCancel_0);
    eventHandlersRef.current = {
      mapClick: mapClickHandler,
      escape: handleEscape_0,
      clickOutside: handleClickOutside_0
    };
    map.on('click', mapClickHandler);
    document.addEventListener('keydown', handleEscape_0);
    document.addEventListener('click', handleClickOutside_0);
    setCursor('crosshair');
  }, [map, createSharedHandlers, setCursor]);
  const create3dObjectAtCoordinates = useCallback(async (lng: number, lat: number, properties?: Partial<MapData3dObject['properties']>) => {
    try {
      const result = await dispatch(createMapObject3d({
        lng,
        lat,
        properties
      })).unwrap();
      toast({
        title: '3D Object created',
        variant: 'success'
      });
      if (result?.properties?.externalId) {
        dispatch(setSelectedMapObject3dId(result.properties.externalId));
      }
      return result;
    } catch (error) {
      toast({
        title: 'Failed to create 3D object',
        description: JSON.stringify(error),
        variant: 'error'
      });
      return null;
    }
  }, [dispatch]);
  const startAdding3dObject = useCallback((defaultProperties?: Partial<MapData3dObject['properties']>, defaultPosition?: [number, number, number]) => {
    if (!map || !externalId) return;
    dispatch(setIsAddingMapObject3d(true));
    const handleCancel = () => {
      dispatch(cancelAddingMapObject3d());
      cleanupEventListeners();
    };
    if (defaultPosition) {
      const [lng_0, lat_0] = defaultPosition;
      void (async () => {
        try {
          await create3dObjectAtCoordinates(lng_0, lat_0, defaultProperties);
          toast({
            title: '3D Object created at copied position',
            variant: 'success'
          });
        } finally {
          handleCancel();
        }
      })();
      return;
    }
    const handleMapClick = async (e_1: maplibregl.MapMouseEvent) => {
      const {
        lng: lng_1,
        lat: lat_1
      } = e_1.lngLat;
      try {
        await create3dObjectAtCoordinates(lng_1, lat_1, defaultProperties);
      } finally {
        handleCancel();
      }
    };
    setupEventListeners(handleMapClick, handleCancel);
    toast({
      title: 'Click to place 3D object',
      description: 'Press ESC to cancel',
      variant: 'info'
    });
  }, [map, externalId, cleanupEventListeners, create3dObjectAtCoordinates, setupEventListeners, dispatch]);
  const cancelAdding3dObject = useCallback(() => {
    dispatch(cancelAddingMapObject3d());
    cleanupEventListeners();
  }, [cleanupEventListeners, dispatch]);
  return {
    isAdding3dObject,
    startAdding3dObject,
    cancelAdding3dObject
  };
}